var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Vector3 } from "flavr-math";
import { FlyToDestinationMessage, SetPlayerWorldTransformMessage, UpdatePlatformTransformMessage } from "@messages/locomotion/LocomotionMessages";
import { MovementMode, RequestMovementSettingsMessage, ResponseMovementSettingsMessage } from "@messages/movement/MovementMessages";
import { SetSkyboxRotation, IsXrPresentingAnswerMessage, IsXrPresentingRequestMessage } from "@messages/rendering/MeshMessages";
import { RequestIsAttachedLocalPlayerMessage, ResponseIsAttachedLocalPlayerMessage } from "@messages/attachment/AttachmentMessage";
import { busService } from "@modules/bus";
import { modelPool } from "@modules/model";
import { resolveUid } from "../model/Utils";
import { scriptService } from "../services/ScriptService";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
const busConnection = busService.connection("ScriptPlayer");
const tmpDirection = new Vector3();
const DEFAULT_HEAD_HEIGHT = 1.7;
export class ScriptPlayer {
    static isXrPlayer() {
        return __awaiter(this, void 0, void 0, function* () {
            const isXrPresentingAnswer = yield busConnection.askByMessageType(new IsXrPresentingRequestMessage(), IsXrPresentingAnswerMessage);
            if (isXrPresentingAnswer !== undefined)
                return isXrPresentingAnswer.xrPresenting;
            return false;
        });
    }
    static getCameraWorldPosition() {
        return localPlayerContainer.cameraWorldPosition;
    }
    static getCameraWorldOrientation() {
        return localPlayerContainer.cameraWorldOrientation;
    }
    static getCameraWorldDirection() {
        return localPlayerContainer.cameraWorldViewDirection;
    }
    static getPlatformScale() {
        return localPlayerContainer.getPlatformScale();
    }
    static fetchMovementSettings() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield busConnection.askByMessageType(new RequestMovementSettingsMessage(), ResponseMovementSettingsMessage);
                this.movementSettings.isFlying = response.isFlying;
                this.movementSettings.movementMode = response.movementMode;
                this.movementSettings.navMashLayerId = response.navMashLayerId;
            }
            catch (error) {
                console.error(error);
                return false;
            }
            return true;
        });
    }
    static setNavMeshLayerId(layerId) {
        busConnection.send(new RequestMovementSettingsMessage({
            navMashLayerId: layerId
        }));
        this.movementSettings.navMashLayerId = layerId;
    }
    static getNavMeshLayerId() {
        return this.movementSettings.navMashLayerId;
    }
    static setHeadHeight(height) {
        if (height === undefined) {
            height = DEFAULT_HEAD_HEIGHT;
        }
        busConnection.send(new RequestMovementSettingsMessage({
            headHeight: height
        }));
        this.movementSettings.headHeight = height;
    }
    static getHeadHeight() {
        return this.movementSettings.headHeight;
    }
    static setDesktopMovement(mode) {
        busConnection.send(new RequestMovementSettingsMessage({
            movementMode: mode
        }));
        this.movementSettings.movementMode = mode;
    }
    static getDesktopMovement() {
        return this.movementSettings.movementMode;
    }
    static setCanFly(enable) {
        busConnection.send(new RequestMovementSettingsMessage({
            canFly: enable
        }));
        this.movementSettings.isFlying = enable;
    }
    static getCanFly() {
        return this.movementSettings.isFlying || false;
    }
    static teleportTo(uid, velocity = 1) {
        uid = resolveUid(uid);
        const model = modelPool.getModel(uid);
        if (model === undefined) {
            throw new Error(`teleportTo: can't find transform data for model ${uid}`);
        }
        busConnection.send(new FlyToDestinationMessage(model.getWorldPosition().toArray(), {
            velocity
        }));
    }
    static flyTo(uid, velocity = 1) {
        uid = resolveUid(uid);
        const model = modelPool.getModel(uid);
        if (model === undefined) {
            throw new Error(`teleportTo: can't find tranform data for model ${uid}`);
        }
        busConnection.send(new FlyToDestinationMessage(model.getWorldPosition().toArray(), {
            velocity
        }));
    }
    static setWorldPosition(pos) {
        busConnection.send(new SetPlayerWorldTransformMessage({
            position: pos.toArray()
        }));
    }
    static setWorldYaw(angle) {
        busConnection.send(new SetPlayerWorldTransformMessage({
            angle
        }));
    }
    static setWorldTransform(pos, yawAngle) {
        busConnection.send(new SetPlayerWorldTransformMessage({
            position: pos.toArray(),
            angle: yawAngle
        }));
    }
    static setWorldTransformToTarget(target) {
        if (scriptService.context === undefined)
            return false;
        const modelUID = resolveUid(target);
        const model = modelPool.getModel(modelUID);
        if (model === undefined)
            return false;
        tmpDirection.set(0, 0, -1).applyQuaternion(model.getWorldOrientation());
        const yaw = Math.atan2(-tmpDirection.x, -tmpDirection.z);
        busConnection.send(new SetPlayerWorldTransformMessage({
            position: model.getWorldPosition().toArray(),
            angle: yaw
        }));
        return true;
    }
    static setScale(scale) {
        if (scriptService.context === undefined)
            return false;
        scriptService.context.connection.send(new SetPlayerWorldTransformMessage({ scale }));
    }
    static attach(uid, position, orientation, scale) {
        if (!scriptService.context)
            return false;
        uid = resolveUid(uid);
        console.error(`AttachToLocalPlayerMessage not yet implemented!!! ${uid}`, position, orientation, scale);
        // scriptService.context.connection.send(new AttachToLocalPlayerMessage(uid, position.toArray(), convertToNumber4(orientation), scale.toArray()))
    }
    static detach(uid) {
        if (!scriptService.context)
            return false;
        uid = resolveUid(uid);
        console.error(`DetachFromLocalPlayerMessage not yet implemented!!! ${uid}`);
        // scriptService.context.connection.send(new DetachFromLocalPlayerMessage(uid))
    }
    static isAttached(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!scriptService.context)
                return false;
            uid = resolveUid(uid);
            try {
                const response = yield scriptService.context.connection.askByMessageType(new RequestIsAttachedLocalPlayerMessage(uid), ResponseIsAttachedLocalPlayerMessage);
                return response.isAttached;
            }
            catch (_a) {
                return false;
            }
        });
    }
    static setPlatformTransform(position, yawAngle, scale = 1, rotateSkybox = true) {
        if (!scriptService.context)
            return false;
        scriptService.context.connection.send(new UpdatePlatformTransformMessage({
            position: position.toArray(),
            angle: yawAngle,
            scale
        }));
        if (rotateSkybox) {
            scriptService.context.connection.send(new SetSkyboxRotation(yawAngle));
        }
        return true;
    }
}
ScriptPlayer.movementSettings = new ResponseMovementSettingsMessage(0, MovementMode.FirstPerson, false, DEFAULT_HEAD_HEIGHT);
